import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import {
  AddressLookupService,
  AddressSuggestion,
  ResolvedAddress,
} from '../../../services/address-lookup.service';

/**
 * Search box that resolves a typed address into structured fields via Photon.
 *
 * Deliberately does NOT own the address form controls — it emits the resolved
 * address and the parent form patches its own fields. That keeps every field
 * hand-editable afterwards (Photon has gaps, especially on house numbers) and
 * keeps this component reusable for any address form.
 */
@Component({
  selector: 'app-address-autocomplete',
  templateUrl: './address-autocomplete.component.html',
  styleUrl: './address-autocomplete.component.scss',
})
export class AddressAutocompleteComponent implements OnInit, OnDestroy {
  @Input() disabled = false;
  @Output() addressResolved = new EventEmitter<ResolvedAddress>();

  searchControl = new FormControl('');
  suggestions: AddressSuggestion[] = [];
  searching = false;

  private destroy$ = new Subject<void>();

  constructor(
    private addressLookup: AddressLookupService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(
        // 300ms is the sweet spot: fast enough to feel live, slow enough that a
        // full street name costs a handful of requests instead of one per key.
        debounceTime(300),
        distinctUntilChanged(),
        tap((value) => (this.searching = (value || '').trim().length >= 3)),
        switchMap((value) =>
          this.addressLookup.search(
            value || '',
            this.translate.currentLang || this.translate.defaultLang || 'de'
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.suggestions = results;
        this.searching = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSelect(suggestion: AddressSuggestion): void {
    if (!suggestion) {
      return;
    }
    const { label, ...address } = suggestion;
    this.addressResolved.emit(address);
    // Clear the box: it is a search field, not a value holder. Leaving the
    // picked label in place reads as if it were saved data.
    this.searchControl.setValue('', { emitEvent: false });
    this.suggestions = [];
  }

  /** nb-autocomplete writes the selected value back into the input; render the
   * label rather than "[object Object]" for the frame before we clear it. */
  displayFn = (value: AddressSuggestion | string): string =>
    typeof value === 'string' ? value : value?.label || '';
}
